import { formatDate, NgClass } from "@angular/common";
import { AfterViewInit, Component, DoCheck, ElementRef, EventEmitter, inject, Input, LOCALE_ID, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ControlContainer, FormsModule, NgModelGroup, ReactiveFormsModule } from "@angular/forms";
import { ClassDirective } from "@ngbracket/ngx-layout/extended";
import { LayoutDirective } from "@ngbracket/ngx-layout/flex";
import { KeyHandlerService, SecurityService } from "@tylertech/core-module";
import { ForgeCircularProgressModule, ForgeDatePickerProxyModule, ForgeTextFieldModule, ForgeTimePickerProxyModule } from "@tylertech/forge-angular";
import { EMPTY_GUID, GuidHelper, Utilities } from "@tylertech/shared-module";
import { PropertyMetadata } from "@tylertech/tyler-api-types";
import { Subject } from "rxjs";
import { debounceTime } from "rxjs/operators";
import { FocusOnLoadDirective } from "../../../../directives/focusOnLoad.directive";
import { AugmentedModelObject, ValueChangedParms } from "../../../../interfaces";
import { MetadataPropertyService, MetadataService } from "../../../../services";
import { ObjectHelpers } from "../../../../utilities";
import { FieldDensity } from "@tylertech/forge";

const VALID_DATE_PATTERN = /\d{1,2}\/?\d{1,2}\/?\d{4}/;
const BEGINNING_OF_WEEK = "bow";
const END_OF_WEEK = "eow";
const BEGINNING_OF_MONTH = "bom";
const END_OF_MONTH = "eom";
const BEGINNING_OF_LAST_MONTH = "bolm";
const END_OF_LAST_MONTH = "eolm";
const BEGINNING_OF_QUARTER = "boq";
const END_OF_QUARTER = "eoq";
const BEGINNING_OF_YEAR = "boy";
const END_OF_YEAR = "eoy";
const BEGINNING_OF_LAST_QUARTER = "bolq";
const END_OF_LAST_QUARTER = "eolq";
const BEGINNING_OF_LAST_YEAR = "boly";
const END_OF_LAST_YEAR = "eoly";

@Component({
    selector: "metadata-input-date-time",
    templateUrl: "./metadata-input-date-time.tmpl.html",
    viewProviders: [{ provide: ControlContainer, useExisting: NgModelGroup }],
    imports: [
        ClassDirective,
        LayoutDirective,
        FocusOnLoadDirective,
        ForgeCircularProgressModule,
        ForgeDatePickerProxyModule,
        ForgeTextFieldModule,
        ForgeTimePickerProxyModule,
        FormsModule,
        NgClass,
        ReactiveFormsModule,
    ],
})
export class MetadataInputDateTimeComponent implements OnInit, AfterViewInit, OnChanges, DoCheck, OnDestroy
{
    private readonly locale = inject(LOCALE_ID);
    private readonly $metadataService = inject(MetadataService);
    private readonly $metadataPropertyService = inject(MetadataPropertyService);
    private readonly $keyHandlerService = inject(KeyHandlerService);
    private readonly $securityService = inject(SecurityService);

    // not really readonly, but we should treat it as immutable within this component.
    @Input()
    readonly model: Date | string;

    @Input()
    metadataModel: AugmentedModelObject; // model should be a field on metadataModel, FYI

    @Input()
    parent: AugmentedModelObject; // metadataModel should be a field on parent, FYI

    @Input()
    propertyMetadata: PropertyMetadata;

    @Input()
    viewOnly?: boolean;

    @Input()
    dense?: boolean;

    @Input()
    compact?: boolean;

    @Input()
    forCellEdit?: boolean;

    @Output()
    onValueChanged = new EventEmitter<ValueChangedParms>();

    @Input()
    hint: string;

    @Input()
    error: string;

    @Input()
    showSpinner: boolean;

    @ViewChild("pickerTextField") pickerTextField: ElementRef;

    /** Accessible in the template */ readonly placeHolder = "MM/dd/yyyy";

    /** Accessible in the template */ isViewOnly: boolean;

    /** Accessible in the template */ isCalendarOpen = false;

    private _valueModel: string;
    private VALID_TIME_PATTERN = /^(1[0-2]|0?[1-9]):[0-5][0-9] (AM|PM)$/;

    public allowSeconds: boolean = false;

    private _initialized: boolean = false;

    /**
     * Metadata's Recalculate on change functionality mutates the metadataModel in a way that will not trigger a binding change event,
     * so we need to have a way to do a simple check in `$doCheck` to determine if the `this.model` value was changed with out our notice.
     * This should only be set in `$onInit` and `onModelChanged`.
     */
    private unboundModel: Date | string;

    private readonly debouncer$ = new Subject<void>();

    density: FieldDensity;

    constructor()
    {
        this.debouncer$.pipe(debounceTime(100), takeUntilDestroyed()).subscribe(() => this.fireValueChangedImmediate());
    }

    ngOnInit(): void
    {
        const reg = this.$metadataPropertyService.getRegex(this.propertyMetadata, this.metadataModel, this.parent);
        if (!Utilities.IsNullOrUndefined(reg))
        {
            this.VALID_TIME_PATTERN = new RegExp(reg);

            if (this.VALID_TIME_PATTERN.test("12:00:00 AM"))
            {
                this.allowSeconds = true;
            }
        }

        this.updateValueModel(this.model);

        this.density = this.$metadataService.getInputDensity(this.compact);
    }

    ngOnDestroy(): void
    {
        this.pickerTextField = null;
        this.debouncer$.complete();
    }

    ngAfterViewInit(): void
    {
        if (this._initialized)
            return;
        this._initialized = true;
    }

    ngDoCheck(): void
    {
        if (!this._initialized)
        {
            return;
        }
        if (this.unboundModel !== this.model)
        {
            this.updateValueModel(this.model);
        }
        this.isViewOnly =
            (this.viewOnly && !this.propertyMetadata.ViewOnlyEnabled) ||
            this.$metadataPropertyService.isViewOnly(this.propertyMetadata, this.metadataModel, this.parent);
    }

    ngOnChanges(_changes: SimpleChanges): void
    {
        if (!this._initialized)
        {
            return;
        }
        const changedKeys = Object.keys(_changes);
        for (const key of changedKeys)
        {
            switch (key)
            {
                case "model":
                    const change = _changes[key];
                    if (!change.currentValue)
                    {
                        this.valueModel = null;
                        break;
                    }
                    this.updateValueModel(change.currentValue);
                    break;
            }
        }
    }

    get telemeteryName(): string
    {
        return this.DateLabel + " date picker";
    }

    get timeTelemeteryName(): string
    {
        return this.DateLabel + " time picker";
    }

    get valueModel(): string
    {
        return this._valueModel;
    }

    set valueModel(value: string)
    {
        this._valueModel = value;
    }

    private updateValueModel(newValue: Date | string): void
    {
        if (Utilities.IsNullOrUndefined(newValue))
        {
            this.valueModel = "";
            this.timeValue = "";
            this.dateValue = "";
        }
        else
        {
            const currentDate = this.toDate(this.valueModel);
            const newDate = new Date(newValue);
            if (!currentDate || currentDate !== newDate)
            {
                this.unboundModel = newValue;
                this.valueModel = this.formatDateTime(newDate);
                this.timeValue = this.formatTime(newDate);
                this.dateValue = this.formatDate(newDate);
            }
        }
    }

    get minDate(): string
    {
        return this.propertyMetadata.MinDate
            ? this.propertyMetadata.MinDate
            : !this.propertyMetadata.MinValueProperty
                ? null
                : this.$metadataService.DependentModuleValue(
                    this.propertyMetadata.MinValueProperty,
                    this.parent,
                    this.metadataModel
                );
    }

    get maxDate(): string
    {
        return this.propertyMetadata.MaxDate
            ? this.propertyMetadata.MaxDate
            : !this.propertyMetadata.MaxValueProperty
                ? null
                : this.$metadataService.DependentModuleValue(
                    this.propertyMetadata.MaxValueProperty,
                    this.parent,
                    this.metadataModel
                );
    }

    get isRequired(): boolean
    {
        return this.$metadataPropertyService.isRequired(this.propertyMetadata, this.metadataModel, this.parent) && !this.isViewOnly;
    }

    get Name(): string
    {
        return this.propertyMetadata.Name;
    }

    get dateControlId(): string
    {
        if (Utilities.IsNullOrUndefined(this.metadataModel)) return `txt_date_${this.Name}`;
        let id = this.metadataModel.$controlId;
        if (!Utilities.IsNullOrUndefined(id)) return `txt_date_${this.Name}_${id}`;
        id = this.metadataModel.$controlId = this.metadataModel.Id;
        if (Utilities.IsNullOrUndefinedOrEmpty(id) || id === EMPTY_GUID)
        {
            id = this.metadataModel.$controlId = GuidHelper.newGuid();
        }
        return `txt_date_${this.Name}_${id}`;
    }

    get timeControlId(): string
    {
        if (Utilities.IsNullOrUndefined(this.metadataModel)) return `txt_time_${this.Name}`;
        let id = this.metadataModel.$controlId;
        if (!Utilities.IsNullOrUndefined(id)) return `txt_time_${this.Name}_${id}`;
        id = this.metadataModel.$controlId = this.metadataModel.Id;
        if (Utilities.IsNullOrUndefinedOrEmpty(id) || id === EMPTY_GUID)
        {
            id = this.metadataModel.$controlId = GuidHelper.newGuid();
        }
        return `txt_time_${this.Name}_${id}`;
    }

    get Label(): string
    {
        return this.forCellEdit
            ? ""
            : this.$metadataService.EvaluateString(this.propertyMetadata.Label, this.parent, this.metadataModel);
    }

    get LabelPosition(): string
    {
        return this.forCellEdit ? 'none' : this.compact ? 'block-start' : 'inset';
    }

    get DateLabel(): string
    {
        return !this.forCellEdit ? `${this.Label} Date` : "";
    }

    get TimeLabel(): string
    {
        return !this.forCellEdit ? `${this.Label} Time` : "";
    }

    onInputChange(event): void
    {
        const hasValue = !Utilities.IsNullOrUndefinedOrEmpty(this.valueModel);
        this.pickerTextField.nativeElement.floatLabel = hasValue;

        if (isNaN(parseInt(event.data)))
        {
            event.stopImmediatePropagation();
        }
        this.debouncer$.next();
    }

    fireValueChangedImmediate(): void
    {
        if (this.forCellEdit && VALID_DATE_PATTERN.test(this.dateValue) && this.onValueChanged)
        {
            this.fireValueChanged();
        }
        const today = new Date();
        let fireChange = false;
        const value = Utilities.IsNullOrUndefined(this.dateValue) ? "" : this.dateValue;
        switch (value.toLowerCase())
        {
            case BEGINNING_OF_WEEK: {
                const day = today.getDay();
                this.dateValue = new Date(
                    today.getFullYear(),
                    today.getMonth(),
                    today.getDate() - day
                )
                    .toLocaleDateString("en-US");
                fireChange = true;
                break;
            }
            case END_OF_WEEK: {
                const day = 6 - today.getDay();
                this.dateValue = new Date(
                    today.getFullYear(),
                    today.getMonth(),
                    today.getDate() + day
                )
                    .toLocaleDateString("en-US");
                fireChange = true;
                break;
            }
            case BEGINNING_OF_MONTH: {
                this.dateValue = new Date(today.getFullYear(), today.getMonth(), 1)
                    .toLocaleDateString("en-US");
                fireChange = true;
                break;
            }
            case END_OF_MONTH: {
                this.dateValue = new Date(today.getFullYear(), today.getMonth() + 1, 0)
                    .toLocaleDateString("en-US");
                fireChange = true;
                break;
            }
            case BEGINNING_OF_LAST_MONTH: {
                this.dateValue = new Date(today.getFullYear(), today.getMonth() - 1, 1)
                    .toLocaleDateString("en-US");
                fireChange = true;
                break;
            }
            case END_OF_LAST_MONTH: {
                this.dateValue = new Date(today.getFullYear(), today.getMonth(), 0)
                    .toLocaleDateString("en-US");
                fireChange = true;
                break;
            }
            case BEGINNING_OF_QUARTER: {
                const quarter = Math.floor(today.getMonth() / 3 + 1) - 1;
                this.dateValue = new Date(today.getFullYear(), quarter * 3, 1)
                    .toLocaleDateString("en-US");
                fireChange = true;
                break;
            }
            case END_OF_QUARTER: {
                const quarter = Math.floor(today.getMonth() / 3 + 1) - 1;
                this.dateValue = new Date(today.getFullYear(), quarter * 3 + 3, 0)
                    .toLocaleDateString("en-US");
                fireChange = true;
                break;
            }
            case BEGINNING_OF_YEAR: {
                this.dateValue = new Date(today.getFullYear(), 0, 1)
                    .toLocaleDateString("en-US");
                fireChange = true;
                break;
            }
            case END_OF_YEAR: {
                this.dateValue = new Date(today.getFullYear(), 11, 31)
                    .toLocaleDateString("en-US");
                fireChange = true;
                break;
            }
            case BEGINNING_OF_LAST_QUARTER: {
                const quarter = Math.floor(today.getMonth() / 3 + 1) - 2;
                this.dateValue = new Date(today.getFullYear(), quarter * 3, 1)
                    .toLocaleDateString("en-US");
                fireChange = true;
                break;
            }
            case END_OF_LAST_QUARTER: {
                const quarter = Math.floor(today.getMonth() / 3 + 1) - 2;
                this.dateValue = new Date(today.getFullYear(), quarter * 3 + 3, 0)
                    .toLocaleDateString("en-US");
                fireChange = true;
                break;
            }
            case BEGINNING_OF_LAST_YEAR: {
                this.dateValue = new Date(today.getFullYear() - 1, 0, 1)
                    .toLocaleDateString("en-US");
                fireChange = true;
                break;
            }
            case END_OF_LAST_YEAR: {
                this.dateValue = new Date(today.getFullYear() - 1, 11, 31)
                    .toLocaleDateString("en-US");
                fireChange = true;
                break;
            }
            default:
                break;
        }

        if (fireChange === true)
        {
            this.fireValueChanged();
        }
    }

    fireValueChanged(): void
    {
        //check date, then set date if it's real
        //check time, set time if real


        //if the time is valid but the date is not, date should be like

        if (this.timeValue)
            this.timeValue = this.timeValue.replace('_', '');

        if (VALID_DATE_PATTERN.test(this.dateValue) && this.VALID_TIME_PATTERN.test(this.timeValue))
        {
            this.valueModel = `${this.dateValue} ${this.timeValue}`;
        }

        if (!VALID_DATE_PATTERN.test(this.dateValue))
        {
            this.dateValue = "";
            //this.dateValue = new Date(0).toLocaleDateString("en-US");
        }
        if (!this.VALID_TIME_PATTERN.test(this.timeValue))
        {
            this.timeValue = "";
            //this.timeValue = new Date(0).toLocaleTimeString("en-US");
        }

        const modelAsDate = ObjectHelpers.isDate(this.model) ? this.model : this.toDate(this.model);

        if (MetadataInputDateTimeComponent.areEqual(modelAsDate, this.toDate(this.valueModel)) && this.dateValue && this.timeValue)
        {
            return;
        }

        //if we get here, that means the dates are the same and datevalue and/or timevalue are invalid
        this.valueModel = `${this.dateValue} ${this.timeValue}`;
        const currentDate = this.toDate(this.valueModel);

        if (
            (this.valueModel && !this.dateValue) || // if we have a value that can't be turned into date value, don't push value up.
            !this.onValueChanged
        )
        {
            return;
        }
        this.timeValue = this.formatTime(this.toDate(this.valueModel));
        this.dateValue = this.formatDate(this.toDate(this.valueModel));
        if (!Utilities.IsNullOrUndefined(currentDate) && currentDate.getHours() === 0 && currentDate.getMinutes() === 0)
        {
            currentDate.setMinutes(0, 0, 1); //Update to handle midnight issue
        }
        requestAnimationFrame(() => this.onValueChanged.emit({ value: currentDate }));
    }

    private static areEqual(a: Date, b: Date): boolean
    {
        if (!a && !b) return true;
        if (!a || !b) return false;
        return a.getTime() === b.getTime();
    }

    get showDate(): string
    {
        return this.valueModel;
    }

    private _timeValue: string;

    set timeValue(value: string)
    {
        this._timeValue = value;
    }

    get timeValue(): string
    {
        return this._timeValue;
    }

    private _dateValue: string;

    set dateValue(value: string)
    {
        this._dateValue = value;
    }

    get dateValue(): string
    {
        return this._dateValue;
    }

    private isValidDate(date: Date): boolean
    {
        return !isNaN(date.getTime());
    }

    private toDate(value: string): Date
    {
        const dateTimeString = this.formatDateTime(value);
        if (Utilities.IsNullOrUndefinedOrEmpty(dateTimeString))
            return null;

        const date = new Date(dateTimeString);
        if (!this.isValidDate(date))
            return null;

        return date;
    }

    private formatDateTime(value: Date | string): string
    {
        if (Utilities.IsNullOrUndefined(value) || value === "")
        {
            return "";
        }
        return formatDate(value, "MM/dd/yyyy hh:mm a", this.locale);
    }

    private formatDate(value: Date | string): string
    {
        if (Utilities.IsNullOrUndefined(value))
        {
            return "";
        }
        if (this.isValidDate(new Date(value)))
        {
            return formatDate(value, "MM/dd/yyyy", this.locale);
        }
        else
        {
            return value.toString();
        }
    }

    private formatTime(value: Date | string): string
    {
        if (Utilities.IsNullOrUndefined(value))
        {
            return "12:00 AM";
        }
        const format = `hh:mm${this.allowSeconds === true ? ":ss" : ""} a`;
        return formatDate(value, format, this.locale);
    }

    onKeyDown(event: KeyboardEvent): void
    {
        if (event.code === "NumpadAdd" && this.$securityService.UseTenKeyPlusAsTab)
        {
            event.preventDefault();
            this.$keyHandlerService.selectNextTabbableElement();
        }
    }
}
